/* eslint-disable @typescript-eslint/no-explicit-any */
import PropTypes from 'prop-types';
import { createContext, useContext, useMemo, useCallback } from 'react';

import { getItemFromLocalStorage, useLocalStorage } from './useLocalStorage';

interface AuthenticationProviderProps {
  children: PropTypes.ReactNodeLike;
}

interface Subscription {
  id: number;
  name: string;
  dailyBubble: boolean;
  knowledgeHub: boolean;
  podcast: boolean;
  learn: boolean;
  meditation: boolean;
  createdAt: string;
  updatedAt: string;
}

interface School {
  id: number;
  name: string;
  active: boolean;
  invactivationReason: string | null;
  createdAt: string;
  updatedAt: string;
  wondeApiToken: string | null;
  wondeSchoolId: string | null;
  location: string | null;
  subscription: Subscription;
  code: string;
}

interface WSPermission {
  id: string;
  name: string;
  createdAt: string;
  updatedAt: string;
  dashboard: boolean;
  adminManagement: boolean;
  reportingDashboard: boolean;
  surveyDashboard: boolean;
  meditation: boolean;
  isAdmin: boolean;
  isMAT: boolean;
  announcements: boolean;
  userManagement: boolean;
}

export interface User {
  id: string;
  username: string;
  email: string;
  provider?: string | null;
  resetPasswordToken: string | null;
  confirmationToken: string | null;
  confirmed: boolean | null;
  blocked: boolean | null;
  createdAt: string;
  updatedAt: string;
  profile: string | null;
  onboardHomeCompleted: boolean;
  location: string | null;
  forename: string | null;
  surname: string | null;
  confirmationEmailSent: boolean | null;
  device: string | null;
  school: School;
  ws_permission: WSPermission;
  onboardQuestionsCompleted: boolean;
  isEmotionWheelTrackingDisabled?: boolean;
  onboardMindfulMinuteCompleted?: boolean;
  onboardEmotionWheelCompleted?: boolean;
}

export interface Authentication {
  accessToken: string;
  user: User;
  isAuthenticated: boolean;
}

interface AuthenticationContextType {
  authentication: Authentication;
  setAuthentication: (value: Authentication) => void;
  loginAuth: (value: Authentication) => void;
  logout: () => void;
}

export const AuthenticationContext = createContext({} as AuthenticationContextType);

export const AuthenticationProvider = ({ children }: AuthenticationProviderProps) => {
  const [authentication, setAuthentication] = useLocalStorage('authentication', {
    isAuthenticated: false,
  });

  const loginAuth = useCallback(
    async (authenticationCallback: Authentication) => {
      setAuthentication({
        isAuthenticated: true,
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        accessToken: (authenticationCallback as any).token,
        user: authenticationCallback.user,
      });
    },
    [setAuthentication],
  );

  const logout = useCallback(() => {
    setAuthentication({
      isAuthenticated: false,
    });
    window.location.href = '/login';
  }, [setAuthentication]);

  const value = useMemo(
    () => ({
      authentication,
      setAuthentication,
      loginAuth,
      logout,
    }),
    [authentication, loginAuth, logout, setAuthentication],
  );

  return <AuthenticationContext.Provider value={value}>{children}</AuthenticationContext.Provider>;
};

export const getAuthentication = () => {
  return (getItemFromLocalStorage<Authentication>('authentication') || {}) as Authentication;
};
export const useAuthentication = () => {
  return useContext(AuthenticationContext);
};
